import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './app/App';
import { ToastProvider } from './components/ui';
import { ApiError } from './lib/api';
import { AuthProvider } from './lib/auth';
import { BranchProvider } from './lib/branch';
import { startQueueAutoSync } from './lib/offlineQueue';
import { registerServiceWorker } from './lib/pwa';
import { ThemeProvider } from './lib/theme';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      refetchOnWindowFocus: true,
      // Never retry what the server refused on purpose.
      retry: (n, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && n < 2,
    },
  },
});

/**
 * After a deploy, a tab opened before it still points at the old build's file names; the first screen it
 * lazy-loads (e.g. right after sign-in) is gone from the server and the page stays blank (boss, 11 Oct).
 * Reload once to pick up the new build; a flag in sessionStorage stops a loop if something else is wrong.
 */
function reloadForNewBuild(): void {
  try {
    const last = Number(sessionStorage.getItem('eros.chunkReload') ?? 0);
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem('eros.chunkReload', String(Date.now()));
  } catch { /* storage blocked: still try once */ }
  window.location.reload();
}
window.addEventListener('vite:preloadError', (e) => { e.preventDefault(); reloadForNewBuild(); });
window.addEventListener('unhandledrejection', (e) => {
  const msg = String((e.reason as Error | undefined)?.message ?? e.reason ?? '');
  if (/dynamically imported module|Importing a module script failed|error loading dynamically/i.test(msg)) reloadForNewBuild();
});

async function boot() {
  // Dev-only fake API for building screens before E1 lands: VITE_MOCK_API=1 npm run dev.
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK_API === '1') await (await import('./dev/mockApi')).installMockApi();
  registerServiceWorker();
  startQueueAutoSync();
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <BranchProvider>
                <ToastProvider>
                  <App />
                </ToastProvider>
              </BranchProvider>
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void boot();
